import { useEffect, useMemo, useState } from "react";
import { stageAssignmentsAPI } from "../../api/client";
import { todayString } from "../../utils/dateUtils";
import EligibleUserSelect from "./EligibleUserSelect";

// Surfaces every stage that is live but unstaffed and lets the leader assign
// them in one pass. Without this, a parallel fan-out means repeating the
// select-stage → Assign tab → pick user → pick date loop once per branch.
export default function ReadyToAssignTray({ project, onAssigned }) {
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const readyStages = useMemo(() => {
    return (project?.stageGraph || []).filter((entry) => entry.needsAssignee);
  }, [project?.stageGraph]);

  const readyKey = readyStages.map((entry) => entry.stageId).join("|");

  useEffect(() => {
    // Seed a row per stage, preserving anything already typed.
    setRows((current) => {
      const next = {};
      readyStages.forEach((entry) => {
        next[entry.stageId] = current[entry.stageId] || {
          assigneeId: "",
          startDate: todayString(),
          scheduledStartTime: "09:00",
          isOvertime: false,
        };
      });
      return next;
    });
    setResults(null);
  }, [readyKey]);

  if (!readyStages.length) return null;

  const updateRow = (stageId, patch) =>
    setRows((current) => ({
      ...current,
      [stageId]: { ...current[stageId], ...patch },
    }));

  const filledStageIds = readyStages
    .map((entry) => entry.stageId)
    .filter((stageId) => rows[stageId]?.assigneeId);

  const handleAssignAll = async () => {
    if (!filledStageIds.length) return;
    setSaving(true);
    setResults(null);
    try {
      const payload = filledStageIds.map((stageId) => ({
        stageId,
        ...rows[stageId],
      }));
      const resp = await stageAssignmentsAPI.createBatch(project.id, payload);
      setResults(resp.data);
      if (onAssigned) await onAssigned();
    } catch (err) {
      setResults({
        results: filledStageIds.map((stageId) => ({
          stageId,
          ok: false,
          error:
            err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to assign",
        })),
        assignedCount: 0,
        failedCount: filledStageIds.length,
      });
    } finally {
      setSaving(false);
    }
  };

  const errorByStageId = new Map(
    (results?.results || [])
      .filter((item) => !item.ok)
      .map((item) => [item.stageId, item.error]),
  );

  return (
    <div className="ready-tray">
      <div className="ready-tray-head">
        <div className="ready-tray-title">
          <span className="ready-tray-badge">{readyStages.length}</span>
          <span>
            {readyStages.length === 1
              ? "Stage is live and unassigned"
              : "Stages are live and unassigned"}
          </span>
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="ready-tray-rows">
            {readyStages.map((entry) => {
              const row = rows[entry.stageId] || {};
              const rowError = errorByStageId.get(entry.stageId);
              return (
                <div
                  key={entry.stageId}
                  className={`ready-tray-row${rowError ? " ready-tray-row--error" : ""}`}
                >
                  <div className="ready-tray-stage">
                    <span className="ready-tray-stage-name">
                      {entry.stageName}
                    </span>
                    {entry.isJoin && (
                      <span className="ready-tray-tag">join</span>
                    )}
                  </div>

                  <div className="ready-tray-field ready-tray-field--user">
                    <EligibleUserSelect
                      projectId={project.id}
                      stageId={entry.stageId}
                      value={row.assigneeId}
                      onChange={(assigneeId) =>
                        updateRow(entry.stageId, { assigneeId })
                      }
                      disabled={saving}
                    />
                  </div>

                  <input
                    type="date"
                    className="ready-tray-field date-input"
                    value={row.startDate || ""}
                    min={todayString()}
                    disabled={saving}
                    onChange={(e) =>
                      updateRow(entry.stageId, { startDate: e.target.value })
                    }
                  />

                  <input
                    type="time"
                    className="ready-tray-field time-input"
                    value={row.scheduledStartTime || ""}
                    step="1800"
                    disabled={saving}
                    onChange={(e) =>
                      updateRow(entry.stageId, {
                        scheduledStartTime: e.target.value,
                      })
                    }
                  />

                  {rowError && (
                    <span className="ready-tray-row-error">{rowError}</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="ready-tray-foot">
            {results && (
              <span
                className={
                  results.failedCount
                    ? "ready-tray-summary ready-tray-summary--partial"
                    : "ready-tray-summary"
                }
              >
                {results.assignedCount} assigned
                {results.failedCount ? `, ${results.failedCount} failed` : ""}
              </span>
            )}
            <span className="small-muted">
              Detailed scheduling (overtime, conflicts, end-date preview) stays
              on each stage's Assign tab.
            </span>
            <button
              type="button"
              className="btn-primary"
              disabled={saving || !filledStageIds.length}
              onClick={handleAssignAll}
            >
              {saving
                ? "Assigning..."
                : `Assign ${filledStageIds.length || ""}`.trim()}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
